import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Check,
  ChevronDown,
  Copy,
  Link as LinkIcon,
  Minus,
  PanelLeft,
  PanelLeftClose,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';
import { StatuteRenderer } from '../StatuteRenderer';
import {
  groupPreviewNavSections,
  PREVIEW_NAV_UNGROUPED_KEY,
  resolveLibrarySectionIdFromHash,
} from '../../lib/statuteSections';
import {
  type StatutePreviewUiSection,
  statutePreviewNavRowNumber,
  sectionToStatuteClipboardText,
  writeStatuteClipboard,
} from '../StatutePreviewReader';
import {
  openGlobalLexCounsel,
  registerLexCounselWorkspace,
  useLexCounselStore,
} from '../../store/lexCounselStore';

function sectionTocFallbackLine(section: StatutePreviewUiSection, unitLabel: string): string {
  if (section.tocLine && String(section.tocLine).trim()) return String(section.tocLine).trim();
  const label = String(section.label || '').trim();
  if (!label) return section.title || unitLabel;
  if (label.toLowerCase() === 'preamble') return section.title || 'Preamble';
  return label;
}

function tocRowTitleAttr(section: StatutePreviewUiSection): string | undefined {
  const t = section.tocLine || section.title;
  return t && String(t).trim() ? String(t) : undefined;
}

function splitPartHeading(heading: string): { partLabel: string; partTitle?: string } {
  const t = String(heading || '').trim();
  const m = t.match(/^(Part\s+[A-Za-z0-9IVXLCDM-]+)(?:\s+(.*))?$/i);
  if (!m) return { partLabel: t };
  const partLabel = m[1].trim();
  const partTitle = m[2] && String(m[2]).trim() ? String(m[2]).trim() : undefined;
  return { partLabel, partTitle };
}

function splitChapterHeading(heading: string): { chapterLabel: string; chapterTitle?: string } {
  const t = String(heading || '').trim();
  const m = t.match(/^(Chapter\s+[A-Za-z0-9IVXLCDM-]+)(?:\s+(.*))?$/i);
  if (!m) return { chapterLabel: t };
  const chapterLabel = m[1].trim();
  const chapterTitle = m[2] && String(m[2]).trim() ? String(m[2]).trim() : undefined;
  return { chapterLabel, chapterTitle };
}

function sameText(a?: string, b?: string): boolean {
  const norm = (v?: string) => String(v || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const av = norm(a);
  const bv = norm(b);
  return Boolean(av) && av === bv;
}

function collectRenderableSectionBodyText(node: unknown): string[] {
  if (!node || typeof node !== 'object') return [];
  const obj = node as Record<string, unknown>;
  const lines: string[] = [];

  const content = typeof obj.content === 'string' ? obj.content.trim() : '';
  if (content) lines.push(content);

  const subKeys = ['subsections', 'sub_sections', 'subSections'] as const;
  for (const key of subKeys) {
    const raw = obj[key];
    if (!Array.isArray(raw)) continue;
    for (const sub of raw) {
      if (!sub || typeof sub !== 'object') continue;
      const so = sub as Record<string, unknown>;
      const number = typeof so.number === 'string' ? so.number.trim() : '';
      const text = typeof so.content === 'string' ? so.content.trim() : '';
      if (text) lines.push(number ? `(${number}) ${text}` : text);
      lines.push(...collectRenderableSectionBodyText(so));
    }
    break;
  }

  const clauses = obj.clauses;
  if (Array.isArray(clauses)) {
    for (const clause of clauses) {
      if (!clause || typeof clause !== 'object') continue;
      const co = clause as Record<string, unknown>;
      const marker =
        typeof co.number === 'string'
          ? co.number.trim()
          : typeof co.letter === 'string'
            ? co.letter.trim()
            : typeof co.id === 'string'
              ? co.id.trim()
              : '';
      const text = typeof co.content === 'string' ? co.content.trim() : '';
      if (text) lines.push(marker ? `(${marker}) ${text}` : text);
      lines.push(...collectRenderableSectionBodyText(co));
    }
  }

  const provisos = obj.provisos;
  if (Array.isArray(provisos)) {
    for (const proviso of provisos) {
      if (typeof proviso === 'string') {
        const t = proviso.trim();
        if (t) lines.push(t);
      } else {
        lines.push(...collectRenderableSectionBodyText(proviso));
      }
    }
  }

  const explanations = obj.explanations;
  if (Array.isArray(explanations)) {
    for (const exp of explanations) {
      if (typeof exp === 'string') {
        const t = exp.trim();
        if (t) lines.push(t);
      } else {
        lines.push(...collectRenderableSectionBodyText(exp));
      }
    }
  }

  return lines;
}

function sectionBodyClipboardText(section: StatutePreviewUiSection): string {
  const lines = collectRenderableSectionBodyText(section)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return lines.join('\n\n').trim();
}

function sectionHeadingClipboardText(section: StatutePreviewUiSection): string {
  const badge = String(section.tocSectionBadge || section.label || '').trim();
  const title = String(section.shortTitle || section.title || '').trim();
  if (!badge && !title) return '';
  if (!badge) return title;
  if (!title) return badge;
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
  if (norm(badge) === norm(title)) return title;
  return `${badge} — ${title}`;
}

function sectionClipboardPayload(section: StatutePreviewUiSection): string {
  const heading = sectionHeadingClipboardText(section);
  const body = sectionBodyClipboardText(section);
  if (heading && body) return `${heading}\n\n${body}`;
  return heading || body;
}

function PreviewSectionNavButtonLex({
  section,
  isActive,
  hidePartLabel,
  onSelect,
  unitLabel,
}: {
  section: StatutePreviewUiSection;
  isActive: boolean;
  hidePartLabel: boolean;
  onSelect: (id: string) => void;
  unitLabel: string;
}) {
  const rowTitle = section.shortTitle || section.title || sectionTocFallbackLine(section, unitLabel);
  const showPartLine = Boolean(!hidePartLabel && section.tocPartLabel && !sameText(section.tocPartLabel, rowTitle));
  const hasStack = Boolean(showPartLine);
  const title = tocRowTitleAttr(section);
  const rowNumber = statutePreviewNavRowNumber(section);
  const baseBtn = `flex w-full text-left gap-2 px-2 py-2 rounded-lg transition-colors text-[13px] leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/55 ${
    isActive ? 'bg-slate-900 text-white' : 'text-slate-800 hover:bg-white/60'
  }`;

  if (!hasStack) {
    return (
      <button type="button" title={title} onClick={() => onSelect(String(section.id))} className={baseBtn}>
        {rowNumber ? (
          <span
            className={`w-7 shrink-0 text-right text-xs font-semibold tabular-nums pt-0.5 ${
              isActive ? 'text-slate-300' : 'text-slate-600'
            }`}
          >
            {rowNumber}.
          </span>
        ) : null}
        <span className={`font-semibold min-w-0 flex-1 ${isActive ? 'text-white' : 'text-slate-900'}`}>{rowTitle}</span>
      </button>
    );
  }

  return (
    <button type="button" title={title} onClick={() => onSelect(String(section.id))} className={baseBtn}>
      {rowNumber ? (
        <span
          className={`w-7 shrink-0 text-right text-xs font-semibold tabular-nums pt-0.5 ${
            isActive ? 'text-slate-300' : 'text-slate-600'
          }`}
        >
          {rowNumber}.
        </span>
      ) : null}
      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        {showPartLine ? (
          <span
            className={`text-[10px] font-bold uppercase tracking-[0.08em] ${isActive ? 'text-slate-300' : 'text-slate-600'}`}
          >
            {section.tocPartLabel}
          </span>
        ) : null}
        <span className={`font-semibold ${isActive ? 'text-white' : 'text-slate-900'}`}>{rowTitle}</span>
      </div>
    </button>
  );
}

const serif = { fontFamily: '"Times New Roman", Times, serif' } as const;

export type LexDraftStatuteReaderProps = {
  sections: StatutePreviewUiSection[];
  loading?: boolean;
  error?: string | null;
  unitLabel: 'Section' | 'Article';
  tocVariant?: 'default' | 'qso';
  linkBasePath: string;
  locationHash: string;
  selectedSectionId: string | null;
  onSelectedSectionIdChange: (id: string) => void;
  /** Sections panel visibility (drawer &lt; lg; in-flow column lg+). */
  sectionsPanelOpen: boolean;
  onSectionsPanelOpenChange: (open: boolean) => void;
  syncUrlForSection: (sectionId: string) => void;
  readerTopSlot?: ReactNode;
  statuteId: string;
  isPublic?: boolean;
  onPublicLogin?: () => void;
  onPublicSignup?: () => void;
};

export function LexDraftStatuteReader({
  sections,
  loading = false,
  error = null,
  unitLabel,
  tocVariant = 'default',
  linkBasePath,
  locationHash,
  selectedSectionId,
  onSelectedSectionIdChange,
  sectionsPanelOpen,
  onSectionsPanelOpenChange,
  syncUrlForSection,
  readerTopSlot,
  statuteId,
  isPublic = false,
  onPublicLogin,
  onPublicSignup,
}: LexDraftStatuteReaderProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [lastBulkNavAction, setLastBulkNavAction] = useState<'sections' | 'parts' | null>('parts');
  const [copiedState, setCopiedState] = useState<Record<string, 'content' | 'link' | null>>({});
  const [zoomLevel, setZoomLevel] = useState(100);
  const [allowPaperZoom, setAllowPaperZoom] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [publicAiModalOpen, setPublicAiModalOpen] = useState(false);

  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const documentScrollRef = useRef<HTMLDivElement | null>(null);
  const sectionNodeMapRef = useRef<Record<string, HTMLElement | null>>({});
  /** Avoid IntersectionObserver fighting smooth scroll / hash jump (ms timestamp). */
  const ignoreObserverUntilRef = useRef(0);
  const onSelectedSectionIdChangeRef = useRef(onSelectedSectionIdChange);
  onSelectedSectionIdChangeRef.current = onSelectedSectionIdChange;

  /** Hash / back-forward: sync selection + scroll only when the URL hash (or sections) changes — not when scroll observer updates selection. */
  useEffect(() => {
    if (!sections.length) return;
    const rawHash = locationHash?.replace(/^#/, '');
    if (!rawHash) return;
    const resolved = resolveLibrarySectionIdFromHash(rawHash, sections);
    if (!resolved) return;
    ignoreObserverUntilRef.current = Date.now() + 1000;
    onSelectedSectionIdChangeRef.current(resolved);
    requestAnimationFrame(() => {
      const id = resolveLibrarySectionIdFromHash(rawHash, sections);
      if (!id) return;
      const node = sectionNodeMapRef.current[String(id)];
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [sections, locationHash]);

  /** No hash: ensure a valid default section (e.g. after statute load). */
  useEffect(() => {
    if (!sections.length) return;
    const rawHash = locationHash?.replace(/^#/, '');
    if (rawHash) return;
    if (selectedSectionId && sections.some((s) => String(s.id) === String(selectedSectionId))) return;
    onSelectedSectionIdChangeRef.current(String(sections[0].id));
  }, [sections, locationHash, selectedSectionId]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => {
      setAllowPaperZoom(mq.matches);
      if (!mq.matches) setZoomLevel(100);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (sectionsPanelOpen) onSectionsPanelOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sectionsPanelOpen, onSectionsPanelOpenChange]);

  const navGroups = useMemo(() => groupPreviewNavSections(sections), [sections]);
  const isQsoToc = tocVariant === 'qso';
  const showNavSubheadings = !(navGroups.length === 1 && navGroups[0].key === PREVIEW_NAV_UNGROUPED_KEY);

  const activeGroupKey = useMemo(() => {
    if (!selectedSectionId) return null;
    for (const group of navGroups) {
      if (group.sections.some((s) => String(s.id) === String(selectedSectionId))) return group.key;
    }
    return null;
  }, [navGroups, selectedSectionId]);

  useEffect(() => {
    if (!navGroups.length) {
      setCollapsedGroups({});
      return;
    }
    // Default to "Parts" mode whenever a new TOC loads: show only group headers.
    const allCollapsed = Object.fromEntries(navGroups.map((group) => [group.key, true]));
    setCollapsedGroups(allCollapsed);
    setLastBulkNavAction('parts');
  }, [navGroups]);

  const isGroupCollapsed = (key: string) => Boolean(collapsedGroups[key]);
  const toggleGroupCollapsed = (key: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
    setLastBulkNavAction(null);
  };

  const expandAllGroups = () => {
    if (!navGroups.length) return;
    const allExpanded = Object.fromEntries(navGroups.map((group) => [group.key, false]));
    setCollapsedGroups(allExpanded);
    setLastBulkNavAction('sections');
  };

  const collapseAllGroups = () => {
    if (!navGroups.length) return;
    const allCollapsed = Object.fromEntries(navGroups.map((group) => [group.key, true]));
    setCollapsedGroups(allCollapsed);
    setLastBulkNavAction('parts');
  };

  const openSection = (sectionId: string) => {
    ignoreObserverUntilRef.current = Date.now() + 1000;
    onSelectedSectionIdChange(sectionId);
    const el = sectionNodeMapRef.current[String(sectionId)];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    syncUrlForSection(sectionId);
    if (window.matchMedia('(max-width: 1023px)').matches) onSectionsPanelOpenChange(false);
  };

  const copySectionContent = async (section: StatutePreviewUiSection) => {
    const text = sectionClipboardPayload(section) || sectionToStatuteClipboardText(section);
    const ok = await writeStatuteClipboard(text);
    const key = String(section.id);
    if (ok) {
      setCopiedState((prev) => ({ ...prev, [key]: 'content' }));
      window.setTimeout(() => {
        setCopiedState((prev) => (prev[key] ? { ...prev, [key]: null } : prev));
      }, 1400);
    } else window.alert('Copy failed. Please copy manually.');
  };

  useEffect(() => {
    if (!sections.length) return;
    let observer: IntersectionObserver | null = null;
    let raf2 = 0;

    const attach = () => {
      observer?.disconnect();
      observer = new IntersectionObserver(
        (entries) => {
          if (Date.now() < ignoreObserverUntilRef.current) return;
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (!visible.length) return;
          const id = visible[0].target.getAttribute('data-section-id');
          if (!id) return;
          onSelectedSectionIdChangeRef.current(String(id));
        },
        {
          root: documentScrollRef.current ?? null,
          threshold: [0.25, 0.35, 0.5, 0.65],
          rootMargin: '-10% 0px -58% 0px',
        },
      );

      sections.forEach((section) => {
        const node = sectionNodeMapRef.current[String(section.id)];
        if (node) observer!.observe(node);
      });
    };

    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(attach);
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer?.disconnect();
    };
  }, [sections]);

  const openLexCounselForStatute = () => {
    const section = sections.find((s) => String(s.id) === String(selectedSectionId));
    const sectionLabel = section?.label || section?.title || '';
    registerLexCounselWorkspace({
      screen: 'library',
      draftTitle: sectionLabel ? `${statuteId} — ${sectionLabel}` : statuteId,
    });
    const prompt = sectionLabel
      ? `/research Explain ${sectionLabel} under this statute and cite relevant Pakistani authorities.`
      : `/research Research this statute and cite relevant Pakistani authorities.`;
    useLexCounselStore.getState().setInputDraft(prompt);
    openGlobalLexCounsel();
  };

  const renderNav = () => (
    <>
      {loading ? <p className="px-3 py-2 text-[13px] text-slate-700">Loading…</p> : null}
      {error ? <p className="px-3 py-2 text-[13px] text-rose-700 font-medium">{error}</p> : null}
      {!loading &&
        !error &&
        navGroups.map((group, gi) => (
          <div key={`${group.key}-${gi}`} className="mb-1 last:mb-0">
            {showNavSubheadings ? (
              <button
                type="button"
                onClick={() => toggleGroupCollapsed(group.key)}
                aria-expanded={!isGroupCollapsed(group.key)}
                className={`px-2 ${gi === 0 ? 'pt-1' : 'pt-3'} pb-1 border-t border-slate-200/50 ${
                  gi === 0 ? 'border-t-0' : ''
                } w-full text-left flex items-center justify-between gap-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1`}
              >
                {(() => {
                  const { partLabel, partTitle } = splitPartHeading(group.heading);
                  return (
                    <p className="text-[10px] uppercase tracking-[0.1em] leading-snug">
                      <span className="font-bold text-slate-900">{partLabel}</span>
                      {partTitle ? <span className="font-semibold text-slate-700"> {partTitle}</span> : null}
                    </p>
                  );
                })()}
                <ChevronDown
                  className={`w-3 h-3 text-slate-400 transition-transform shrink-0 ${
                    isGroupCollapsed(group.key) ? '-rotate-90' : 'rotate-0'
                  }`}
                />
              </button>
            ) : null}
            {showNavSubheadings && isGroupCollapsed(group.key) ? null : isQsoToc ? (
              <div className="flex flex-col gap-0.5">
                {(() => {
                  const chapterGroups = group.sections.reduce<Array<{ chapter: string; rows: StatutePreviewUiSection[] }>>(
                    (acc, section) => {
                      const chapter = String(section.tocChapterLabel || '').trim() || 'Articles';
                      const last = acc[acc.length - 1];
                      if (last && last.chapter === chapter) last.rows.push(section);
                      else acc.push({ chapter, rows: [section] });
                      return acc;
                    },
                    [],
                  );
                  return chapterGroups.map((cg, cgi) => (
                    <div key={`${group.key}-${cg.chapter}-${cgi}`} className="pt-0.5">
                      {cg.chapter && cg.chapter !== 'Articles' && !sameText(cg.chapter, group.heading) ? (
                        <div className="px-2 pb-1">
                          {(() => {
                            const { chapterLabel, chapterTitle } = splitChapterHeading(cg.chapter);
                            return (
                              <p className="text-[10px] uppercase tracking-[0.08em] text-slate-800">
                                <span className="font-bold">{chapterLabel}</span>
                                {chapterTitle ? <span className="font-semibold"> {chapterTitle}</span> : null}
                              </p>
                            );
                          })()}
                        </div>
                      ) : null}
                      {(() => {
                        const headingGroups = cg.rows.reduce<Array<{ heading: string; rows: StatutePreviewUiSection[] }>>(
                          (acc, section) => {
                            const heading = String(section.tocHeadingLabel || '').trim();
                            const last = acc[acc.length - 1];
                            if (last && last.heading === heading) last.rows.push(section);
                            else acc.push({ heading, rows: [section] });
                            return acc;
                          },
                          [],
                        );
                        return headingGroups.map((hg, hgi) => (
                          <div key={`${group.key}-${cg.chapter}-${hg.heading || 'nh'}-${hgi}`} className="pb-0.5">
                            {hg.heading && !sameText(hg.heading, cg.chapter) ? (
                              <div className="px-2 pt-0.5 pb-0.5">
                                <p className="text-[10px] font-bold uppercase tracking-[0.09em] text-slate-700">
                                  {hg.heading}
                                </p>
                              </div>
                            ) : null}
                            <div className="flex flex-col gap-0.5">
                              {hg.rows.map((section) => (
                                <PreviewSectionNavButtonLex
                                  key={section.id}
                                  section={section}
                                  isActive={String(section.id) === String(selectedSectionId)}
                                  hidePartLabel
                                  onSelect={openSection}
                                  unitLabel={unitLabel}
                                />
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <div className="flex flex-col gap-0.5">
                {group.sections.map((section) => (
                  <PreviewSectionNavButtonLex
                    key={section.id}
                    section={section}
                    isActive={String(section.id) === String(selectedSectionId)}
                    hidePartLabel={showNavSubheadings && group.key !== PREVIEW_NAV_UNGROUPED_KEY}
                    onSelect={openSection}
                    unitLabel={unitLabel}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
    </>
  );

  const sectionsPanelInner = (
    <>
      <div className="px-4 pt-4 pb-3 border-b border-white/30 flex items-center justify-between gap-2 shrink-0 min-w-0">
        <h3 className="text-[15px] font-bold text-slate-800 tracking-tight truncate">Sections</h3>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onSectionsPanelOpenChange(false)}
            className="hidden lg:flex p-2 rounded-lg text-slate-500 hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            aria-label="Collapse sections"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onSectionsPanelOpenChange(false)}
            className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            aria-label="Close sections"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="px-4 pb-3 border-b border-white/30">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={expandAllGroups}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
              lastBulkNavAction === 'sections'
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-slate-700 border-slate-300 hover:border-primary/40 hover:text-primary'
            }`}
          >
            Sections
          </button>
          <button
            type="button"
            onClick={collapseAllGroups}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
              lastBulkNavAction === 'parts'
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-slate-700 border-slate-300 hover:border-primary/40 hover:text-primary'
            }`}
          >
            Parts
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain py-2 px-1.5 custom-scrollbar min-h-0 min-w-0">
        {renderNav()}
      </div>
    </>
  );

  const paperScale = allowPaperZoom ? zoomLevel / 100 : 1;
  const paperStyle: CSSProperties = {
    ...(allowPaperZoom
      ? {
          transform: `scale(${paperScale})`,
          marginBottom: `${(paperScale - 1) * 1056}px`,
        }
      : {}),
    ...serif,
    fontSize: '13pt',
    lineHeight: 1.45,
    color: '#111827',
  };

  return (
    <div
      ref={contentScrollRef}
      className="relative w-full h-full max-w-full min-w-0 bg-[#F3F4F6] overflow-hidden"
    >
      {/* Drawer dim */}
      <div
        className={`lg:hidden fixed inset-x-0 top-[56px] sm:top-[60px] bottom-16 z-[35] bg-slate-900/25 backdrop-blur-[1px] transition-opacity duration-300 ${
          sectionsPanelOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => onSectionsPanelOpenChange(false)}
        aria-hidden={!sectionsPanelOpen}
      />

      <aside
        id="statute-sections-panel"
        role="navigation"
        aria-label="Statute sections"
        style={serif}
        className={`flex flex-col min-h-0 rounded-2xl border border-white/50 bg-white/95 backdrop-blur-xl shadow-xl overflow-hidden transition-[transform,opacity] duration-300 ease-out
          max-lg:fixed max-lg:z-[40] max-lg:left-0 max-lg:top-[56px] sm:max-lg:top-[60px] max-lg:bottom-16 max-lg:w-[min(20.5rem,calc(100vw-1rem))] max-lg:rounded-r-2xl max-lg:border-l-0
          lg:absolute lg:z-[25] lg:top-4 lg:bottom-4 lg:left-4 lg:w-80
          ${
            sectionsPanelOpen
              ? 'translate-x-0 opacity-100 pointer-events-auto'
              : '-translate-x-[110%] opacity-0 pointer-events-none'
          }`}
      >
        {sectionsPanelInner}
      </aside>

      {!sectionsPanelOpen ? (
        <button
          type="button"
          onClick={() => onSectionsPanelOpenChange(true)}
          className="hidden lg:inline-flex absolute top-4 left-4 z-[24] items-center gap-2 px-3 py-2 rounded-full bg-white/90 backdrop-blur-md border border-white/50 text-slate-800 text-sm font-semibold shadow-md hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          aria-expanded={false}
          aria-controls="statute-sections-panel"
        >
          <PanelLeft className="w-4 h-4" />
          Sections
        </button>
      ) : null}

      <div
        id="statute-scroll-container"
        ref={documentScrollRef}
        className={`absolute inset-0 overflow-y-auto overflow-x-hidden pt-4 pb-40 md:pb-12 transition-[padding] duration-300 ${
          sectionsPanelOpen ? 'lg:pl-[22rem]' : 'lg:pl-4'
        }`}
      >
        <div className="w-full max-w-full min-w-0 flex flex-col items-stretch lg:items-center px-3 sm:px-4 md:px-6 lg:px-2">
          {!sections.length && !loading ? (
            <div className="p-8 sm:p-12 text-center text-slate-600 max-w-lg mx-auto" style={serif}>
              No sections loaded. Check the statute source or try again.
            </div>
          ) : (
            <div
              className="w-full max-w-full min-w-0 lg:w-[816px] lg:max-w-[816px] max-lg:bg-transparent max-lg:border-0 max-lg:shadow-none lg:bg-white lg:border lg:border-[#E5E5EA] lg:shadow-[0_2px_15px_rgba(0,0,0,0.03)] max-lg:py-4 max-lg:px-0 sm:max-lg:px-1 lg:min-h-[1056px] lg:py-[72px] lg:px-[72px] mt-4 lg:mt-6 lg:rounded-sm lg:origin-top"
              style={paperStyle}
            >
              {readerTopSlot ? <div className="mb-6 flex justify-end">{readerTopSlot}</div> : null}
              {sections.map((section) => (
                <article
                  key={section.id}
                  id={`section-${String(section.id).replace(/^section-/i, '')}`}
                  data-section-id={String(section.id)}
                  ref={(node) => {
                    sectionNodeMapRef.current[String(section.id)] = node;
                  }}
                  className="scroll-mt-6 sm:scroll-mt-6 lg:scroll-mt-6 mb-8 sm:mb-10 pb-8 sm:pb-10 last:pb-0 max-w-full min-w-0"
                  style={serif}
                >
                  <div className="flex flex-wrap items-start justify-end gap-2 mb-3">
                    {(() => {
                      const copied = copiedState[String(section.id)];
                      const isContentCopied = copied === 'content';
                      const isLinkCopied = copied === 'link';
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => copySectionContent(section)}
                            className={`text-[11px] font-semibold shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded ${
                              isContentCopied ? 'text-emerald-700' : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            {isContentCopied ? <Check className="w-3 h-3 inline mr-0.5" /> : <Copy className="w-3 h-3 inline mr-0.5" />}
                            {isContentCopied ? 'Copied' : 'Copy'}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              const hashValue = `#section-${String(section.id).replace(/^section-/i, '')}`;
                              const ok = await writeStatuteClipboard(
                                `${window.location.origin}${linkBasePath}${hashValue}`,
                              );
                              const key = String(section.id);
                              if (ok) {
                                setCopiedState((prev) => ({ ...prev, [key]: 'link' }));
                                window.setTimeout(() => {
                                  setCopiedState((prev) => (prev[key] ? { ...prev, [key]: null } : prev));
                                }, 1400);
                              } else window.alert('Copy link failed.');
                            }}
                            className={`text-[11px] font-semibold shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded ${
                              isLinkCopied ? 'text-emerald-700' : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            {isLinkCopied ? <Check className="w-3 h-3 inline mr-0.5" /> : <LinkIcon className="w-3 h-3 inline mr-0.5" />}
                            {isLinkCopied ? 'Link copied' : 'Copy link'}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                  {section.tocPartLabel ? (
                    !sameText(section.tocPartLabel, section.shortTitle || section.title) ? (
                      <p className="text-[11pt] font-extrabold uppercase tracking-[0.12em] text-center text-slate-800 mb-4 break-words">
                        {section.tocPartLabel}
                      </p>
                    ) : null
                  ) : null}
                  {section.tocSectionBadge ? (
                    <p className="text-[12pt] font-bold text-slate-900 mb-2 break-words">{section.tocSectionBadge}</p>
                  ) : null}
                  {!section.tocSectionBadge && section.label ? (
                    <p className="text-[10pt] uppercase tracking-[0.12em] text-slate-700 font-bold mb-1 break-words">{section.label}</p>
                  ) : null}
                  <h2 className="text-[15pt] font-bold text-slate-950 mb-4 leading-snug break-words" style={serif}>
                    {section.shortTitle || section.title}
                  </h2>
                  <div className="min-w-0 max-w-full [&_p]:text-[13pt] [&_p]:leading-[1.45] [&_li]:text-[13pt] [&_li]:leading-[1.45] text-slate-800 break-words [&_a]:break-all [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto">
                    <StatuteRenderer data={section} />
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-20 sm:bottom-24 md:bottom-6 right-3 sm:right-4 md:right-6 z-[40] flex flex-col items-end gap-2 sm:gap-3 pointer-events-none">
        <div className="pointer-events-auto relative">
          {isPublic ? (
            <>
              {publicAiModalOpen ? (
                <div className="mb-2 w-[min(360px,calc(100vw-1.5rem))] bg-white/95 backdrop-blur-xl border border-slate-200/60 shadow-2xl rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200/80 flex items-center justify-between bg-slate-50/80">
                    <span className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary shrink-0" />
                      Sign up to use AI search
                    </span>
                    <button
                      type="button"
                      onClick={() => setPublicAiModalOpen(false)}
                      className="p-1 rounded-lg hover:bg-slate-200 text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      aria-label="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-600">
                      Get instant AI answers to questions about this law across thousands of Pakistani statutes.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={onPublicSignup}
                        className="inline-flex items-center gap-2 text-sm font-semibold bg-zinc-900 text-white px-4 py-2 rounded-lg hover:bg-zinc-800 transition-colors"
                      >
                        Sign up free
                      </button>
                      <button
                        type="button"
                        onClick={onPublicLogin}
                        className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        Log in
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => setPublicAiModalOpen((o) => !o)}
                className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full bg-[#0a0a0a] text-white text-xs sm:text-sm font-bold shadow-lg border border-black hover:bg-zinc-800 transition-all pointer-events-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
                <span className="max-[380px]:sr-only">Sign up for AI search</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={openLexCounselForStatute}
              className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full bg-[#0a0a0a] text-white text-xs sm:text-sm font-bold shadow-lg border border-black hover:bg-zinc-800 transition-all pointer-events-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
              <span className="max-[380px]:sr-only">Ask LexCounsel</span>
            </button>
          )}
        </div>

        <div className="hidden lg:flex items-center gap-1 bg-white/80 backdrop-blur-md border border-slate-200/60 shadow-lg rounded-full p-1 pointer-events-auto">
          <button
            type="button"
            onClick={() => setZoomLevel((prev) => Math.max(50, Math.round(prev / 5) * 5 - 5))}
            className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label="Zoom out"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="w-12 text-center select-none text-xs font-semibold text-slate-700">{Math.round(zoomLevel)}%</span>
          <button
            type="button"
            onClick={() => setZoomLevel((prev) => Math.min(200, Math.round(prev / 5) * 5 + 5))}
            className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label="Zoom in"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
